import { useState, useEffect, useMemo } from 'react';
import { AppointmentType, ExpandableFieldItem } from '@features/appointment-types';
import {
  AppointmentTypeChanges,
  FormChange,
  ReminderChange,
  ModifiedFormChange,
  ModifiedReminderChange,
} from '../types/appointmentChanges';
import { formatAppointmentDuration } from '../../../utils/durationUtils';
import {
  doCptCodesMatch,
  getTelehealthForModality,
  type VisitModality,
} from '../../../utils/modalityPlaceOfService';

export interface UseDirtyStateTrackingProps {
  selectedAppointmentType?: AppointmentType;
  selectedDuration?: number;
  currentTelehealth?: boolean;
  currentModality?: VisitModality;
  currentPlaceOfService?: string | null;
  currentCptCodes?: string[] | null;
  currentUseJotmeet?: boolean | null;
  currentForms?: ExpandableFieldItem[];
  currentReminders?: ExpandableFieldItem[];
}

export const useDirtyStateTracking = ({
  selectedAppointmentType,
  selectedDuration,
  currentTelehealth,
  currentModality,
  currentPlaceOfService,
  currentCptCodes = [],
  currentUseJotmeet = false,
  currentForms = [],
  currentReminders = [],
}: UseDirtyStateTrackingProps) => {
  const [dirtyFields, setDirtyFields] = useState({
    duration: false,
    telehealth: false,
    session: false,
    forms: false,
    reminders: false,
  });
  const [showDirtyAlert, setShowDirtyAlert] = useState(false);

  // Mark specific field as dirty
  const markDirty = (field: keyof typeof dirtyFields) => {
    setDirtyFields(prev => ({ ...prev, [field]: true }));
  };

  // Check if a field is dirty
  const isDirty = (field: keyof typeof dirtyFields) => dirtyFields[field];

  // Reset all dirty state
  const resetDirtyState = () => {
    setDirtyFields({
      duration: false,
      telehealth: false,
      session: false,
      forms: false,
      reminders: false,
    });
  };

  // Helper function to check if current forms match appointment type defaults
  const doCurrentFormsMatchDefaults = (
    appointmentType: AppointmentType,
    forms: ExpandableFieldItem[],
  ): boolean => {
    const defaultFormRules = appointmentType.default_form_rules || [];

    if (defaultFormRules.length !== forms.length) {
      return false;
    }

    return defaultFormRules.every((rule, index) => {
      const currentForm = forms[index];
      return (
        currentForm &&
        currentForm.formId === rule.form_definition_id &&
        currentForm.offset === rule.offset
      );
    });
  };

  // Helper function to check if current reminders match appointment type defaults
  const doCurrentRemindersMatchDefaults = (
    appointmentType: AppointmentType,
    reminders: ExpandableFieldItem[],
  ): boolean => {
    const defaultReminderRules = appointmentType.default_reminder_rules || [];

    if (defaultReminderRules.length !== reminders.length) {
      return false;
    }

    return defaultReminderRules.every((rule, index) => {
      const currentReminder = reminders[index];
      return (
        currentReminder &&
        currentReminder.name === rule.reminder_template_id &&
        currentReminder.offset === rule.offset
      );
    });
  };

  // Auto-clear dirty flags when values match appointment type defaults
  useEffect(() => {
    if (!selectedAppointmentType) return;

    const newDirtyFields = { ...dirtyFields };

    // Check duration
    if (dirtyFields.duration) {
      const defaultDuration = selectedAppointmentType.default_duration;
      if (selectedDuration === defaultDuration) {
        newDirtyFields.duration = false;
      }
    }

    // Check telehealth
    if (dirtyFields.telehealth) {
      const defaultTelehealth = selectedAppointmentType.telehealth ?? false;
      if (currentTelehealth === defaultTelehealth) {
        newDirtyFields.telehealth = false;
      }
    }

    if (dirtyFields.session) {
      const defaultModality =
        selectedAppointmentType.default_modality ??
        (selectedAppointmentType.telehealth ? 'video' : null);
      const defaultPlaceOfService = selectedAppointmentType.default_place_of_service ?? null;
      const defaultCptCodes = selectedAppointmentType.default_cpt_codes ?? [];
      const defaultUseJotmeet = Boolean(
        selectedAppointmentType.default_use_jotmeet && defaultModality === 'video',
      );

      if (
        currentModality === defaultModality &&
        (currentPlaceOfService ?? null) === defaultPlaceOfService &&
        doCptCodesMatch(currentCptCodes, defaultCptCodes) &&
        Boolean(currentUseJotmeet) === defaultUseJotmeet
      ) {
        newDirtyFields.session = false;
      }
    }

    // Check forms
    if (dirtyFields.forms) {
      const currentFormsMatch = doCurrentFormsMatchDefaults(selectedAppointmentType, currentForms);
      if (currentFormsMatch) {
        newDirtyFields.forms = false;
      }
    }

    // Check reminders
    if (dirtyFields.reminders) {
      const currentRemindersMatch = doCurrentRemindersMatchDefaults(
        selectedAppointmentType,
        currentReminders,
      );
      if (currentRemindersMatch) {
        newDirtyFields.reminders = false;
      }
    }

    // Update dirty fields if any changed
    if (
      newDirtyFields.duration !== dirtyFields.duration ||
      newDirtyFields.telehealth !== dirtyFields.telehealth ||
      newDirtyFields.session !== dirtyFields.session ||
      newDirtyFields.forms !== dirtyFields.forms ||
      newDirtyFields.reminders !== dirtyFields.reminders
    ) {
      setDirtyFields(newDirtyFields);
    }
  }, [
    selectedAppointmentType,
    selectedDuration,
    currentTelehealth,
    currentModality,
    currentPlaceOfService,
    currentCptCodes,
    currentUseJotmeet,
    currentForms,
    currentReminders,
    dirtyFields,
  ]);

  // Show/hide alert based on dirty state
  useEffect(() => {
    const hasDirtyChanges =
      dirtyFields.duration ||
      dirtyFields.telehealth ||
      dirtyFields.session ||
      dirtyFields.forms ||
      dirtyFields.reminders;
    setShowDirtyAlert(hasDirtyChanges);
  }, [dirtyFields]);

  // Compute actual changes
  const changes = useMemo<AppointmentTypeChanges | undefined>(() => {
    if (!selectedAppointmentType || !showDirtyAlert) return undefined;

    const computedChanges: AppointmentTypeChanges = {};

    // Track duration changes
    if (dirtyFields.duration && selectedDuration !== undefined) {
      const defaultDuration = selectedAppointmentType.default_duration || 30;
      if (selectedDuration !== defaultDuration) {
        computedChanges.duration = {
          old: defaultDuration,
          new: selectedDuration,
        };
      }
    }

    // Track telehealth changes
    if (dirtyFields.telehealth && currentTelehealth !== undefined) {
      const defaultTelehealth = selectedAppointmentType.telehealth ?? false;
      if (currentTelehealth !== defaultTelehealth) {
        computedChanges.telehealth = {
          old: defaultTelehealth,
          new: currentTelehealth,
        };
      }
    }

    if (dirtyFields.session) {
      const defaultModality =
        selectedAppointmentType.default_modality ??
        (selectedAppointmentType.telehealth ? 'video' : null);
      const defaultTelehealth = getTelehealthForModality(defaultModality);
      const nextTelehealth = getTelehealthForModality(currentModality);
      const defaultPlaceOfService = selectedAppointmentType.default_place_of_service ?? null;
      const defaultCptCodes = selectedAppointmentType.default_cpt_codes ?? [];
      const defaultUseJotmeet = Boolean(
        selectedAppointmentType.default_use_jotmeet && defaultModality === 'video',
      );
      const nextUseJotmeet = Boolean(currentUseJotmeet);

      if (currentModality !== defaultModality) {
        computedChanges.modality = {
          old: defaultModality,
          new: currentModality ?? null,
        };
      }

      if (nextTelehealth !== defaultTelehealth) {
        computedChanges.telehealth = {
          old: defaultTelehealth,
          new: nextTelehealth,
        };
      }

      if ((currentPlaceOfService ?? null) !== defaultPlaceOfService) {
        computedChanges.placeOfService = {
          old: defaultPlaceOfService,
          new: currentPlaceOfService ?? null,
        };
      }

      if (!doCptCodesMatch(currentCptCodes, defaultCptCodes)) {
        computedChanges.cptCodes = {
          old: defaultCptCodes,
          new: currentCptCodes ?? [],
        };
      }

      if (nextUseJotmeet !== defaultUseJotmeet) {
        computedChanges.useJotmeet = {
          old: defaultUseJotmeet,
          new: nextUseJotmeet,
        };
      }
    }

    // Track form changes
    if (dirtyFields.forms) {
      const defaultForms = selectedAppointmentType.default_form_rules || [];
      const currentFormIds = new Set(currentForms.map(f => f.formId));
      const defaultFormIds = new Set(defaultForms.map(f => f.form_definition_id));

      const added: FormChange[] = [];
      const removed: FormChange[] = [];
      const modified: ModifiedFormChange[] = [];

      // Find added and modified forms
      currentForms.forEach(currentForm => {
        const defaultForm = defaultForms.find(df => df.form_definition_id === currentForm.formId);

        if (!defaultForm) {
          // Form was added
          added.push({
            name: currentForm.name,
            timing: formatAppointmentDuration(currentForm.offset || ''),
            formId: currentForm.formId,
          });
        } else if (defaultForm.offset !== currentForm.offset) {
          // Form timing was modified
          modified.push({
            name: currentForm.name,
            oldTiming: formatAppointmentDuration(defaultForm.offset),
            newTiming: formatAppointmentDuration(currentForm.offset || ''),
            formId: currentForm.formId,
          });
        }
      });

      // Find removed forms
      defaultForms.forEach(defaultForm => {
        if (!currentFormIds.has(defaultForm.form_definition_id)) {
          removed.push({
            name: defaultForm.title || 'Unknown Form',
            timing: formatAppointmentDuration(defaultForm.offset),
            formId: defaultForm.form_definition_id,
          });
        }
      });

      if (added.length || removed.length || modified.length) {
        computedChanges.forms = { added, removed, modified };
      }
    }

    // Track reminder changes
    if (dirtyFields.reminders) {
      const defaultReminders = selectedAppointmentType.default_reminder_rules || [];

      const added: ReminderChange[] = [];
      const removed: ReminderChange[] = [];
      const modified: ModifiedReminderChange[] = [];

      // Compare reminder counts and timings
      currentReminders.forEach((current, idx) => {
        const defaultReminder = defaultReminders[idx];

        if (!defaultReminder) {
          // Reminder was added
          added.push({
            timing: formatAppointmentDuration(current.offset || ''),
          });
        } else if (defaultReminder.offset !== current.offset) {
          // Reminder timing was modified
          modified.push({
            oldTiming: formatAppointmentDuration(defaultReminder.offset),
            newTiming: formatAppointmentDuration(current.offset || ''),
          });
        }
      });

      // Find removed reminders
      if (defaultReminders.length > currentReminders.length) {
        for (let i = currentReminders.length; i < defaultReminders.length; i++) {
          removed.push({
            timing: formatAppointmentDuration(defaultReminders[i].offset),
          });
        }
      }

      if (added.length || removed.length || modified.length) {
        computedChanges.reminders = { added, removed, modified };
      }
    }

    return Object.keys(computedChanges).length > 0 ? computedChanges : undefined;
  }, [
    selectedAppointmentType,
    selectedDuration,
    currentTelehealth,
    currentModality,
    currentPlaceOfService,
    currentCptCodes,
    currentUseJotmeet,
    currentForms,
    currentReminders,
    dirtyFields,
    showDirtyAlert,
  ]);

  return {
    // State
    dirtyFields,
    showDirtyAlert,
    changes,

    // Actions
    markDirty,
    isDirty,
    resetDirtyState,

    // Utilities
    doCurrentFormsMatchDefaults,
    doCurrentRemindersMatchDefaults,
  };
};

export type UseDirtyStateTrackingReturn = ReturnType<typeof useDirtyStateTracking>;
